import type {
  BirthdayMembersDto,
  SlotParticipantLightDto,
  TrialsDropInsBirthdaysDto,
  WeeklyTrialsDropInsBirthdaysDto,
} from "@/modules/boxDashboard/gateway/boxDashboardApiDto"
import { Language } from "@/modules/language/utils/languageUtils"
import dayjs from "dayjs"
import isSameOrAfter from "dayjs/plugin/isSameOrAfter"
import isSameOrBefore from "dayjs/plugin/isSameOrBefore"

dayjs.extend(isSameOrAfter)
dayjs.extend(isSameOrBefore)

export class SlotParticipantEntity {
  constructor(
    public readonly boxMemberId: string,
    public readonly username: string | null,
    public readonly firstname: string | null,
    public readonly lastname: string | null,
    public readonly slotStart: string | null,
    public readonly programName: string | null,
    public readonly coachFirstname: string | null,
    public readonly coachLastname: string | null,
  ) {}

  static fromDto(dto: SlotParticipantLightDto): SlotParticipantEntity {
    return new SlotParticipantEntity(
      dto.boxMemberId,
      dto.username,
      dto.firstname,
      dto.lastname,
      dto.slotStart,
      dto.programName,
      dto.assignedStaffMembers?.firstname ?? null,
      dto.assignedStaffMembers?.lastname ?? null,
    )
  }

  getAthleteFullName(): string {
    return `${this.firstname ?? ""} ${this.lastname?.charAt(0) ?? ""}.`
  }

  getCoachFullName(): string | null {
    if (!this.coachFirstname || !this.coachLastname) return null
    return `${this.coachFirstname} ${this.coachLastname.charAt(0)}.`
  }

  getTime(): string | null {
    return this.slotStart ? dayjs(this.slotStart).format("LT") : null
  }
}

export class BirthdayMemberEntity {
  constructor(
    public readonly boxMemberId: string,
    public readonly username: string | null,
    public readonly firstname: string | null,
    public readonly lastname: string | null,
    public readonly slotStart: string | null,
    public readonly programName: string | null,
    public readonly coachFirstname: string | null,
    public readonly coachLastname: string | null,
    public readonly lastSessionDate: string | null,
    public readonly nextSessionDate: string | null,
  ) {}

  static fromDto(dto: BirthdayMembersDto): BirthdayMemberEntity {
    return new BirthdayMemberEntity(
      dto.boxMemberId,
      dto.username,
      dto.firstname,
      dto.lastname,
      dto.slotStart,
      dto.programName,
      dto.assignedStaffMembers?.firstname ?? null,
      dto.assignedStaffMembers?.lastname ?? null,
      dto.lastSessionDate ?? null,
      dto.nextSessionDate ?? null,
    )
  }

  getAthleteFullName(): string {
    return `${this.firstname ?? ""} ${this.lastname?.charAt(0) ?? ""}.`
  }

  getCoachFullName(): string | null {
    if (!this.coachFirstname || !this.coachLastname) return null
    return `${this.coachFirstname} ${this.coachLastname.charAt(0)}.`
  }

  getTime(): string | null {
    return this.slotStart ? dayjs(this.slotStart).format("LT") : null
  }

  getNextSessionDateFormated(): string | null {
    return this.nextSessionDate ? dayjs(this.nextSessionDate).format("L") : null
  }

  getLastSessionDateInDays(): number | null {
    if (!this.lastSessionDate) return null
    return dayjs().diff(dayjs(this.lastSessionDate), "day")
  }
}

export class DayDataEntity {
  constructor(
    public readonly date: string,
    public readonly numberOfTrials: number,
    public readonly numberOfDropIns: number,
    public readonly numberOfBirthdays: number,
    public readonly trials: SlotParticipantEntity[],
    public readonly dropIns: SlotParticipantEntity[],
    public readonly birthdays: BirthdayMemberEntity[],
  ) {}

  static fromDto(dto: TrialsDropInsBirthdaysDto): DayDataEntity {
    return new DayDataEntity(
      dto.dayDate,
      dto.numberOfTrials ?? 0,
      dto.numberOfDropIns ?? 0,
      dto.numberOfBirthdays ?? 0,
      dto.trials?.map((t) => SlotParticipantEntity.fromDto(t)) ?? [],
      dto.dropIns?.map((d) => SlotParticipantEntity.fromDto(d)) ?? [],
      dto.birthdays?.map((b) => BirthdayMemberEntity.fromDto(b)) ?? [],
    )
  }

  isToday(): boolean {
    return dayjs(this.date).isSame(dayjs(), "day")
  }

  getDayOfWeek(): string {
    const day = dayjs(this.date)
    return day.format("ddd")
  }

  getDayOfMonth(): number {
    return dayjs(this.date).date()
  }

  hasTrials(): boolean {
    return this.numberOfTrials > 0
  }

  hasDropIns(): boolean {
    return this.numberOfDropIns > 0
  }

  hasBirthdays(): boolean {
    return this.numberOfBirthdays > 0
  }

  getBirthdaysPresent(): BirthdayMemberEntity[] {
    return this.birthdays.filter((b) => b.slotStart !== null)
  }

  getBirthdaysAbsent(): BirthdayMemberEntity[] {
    return this.birthdays.filter((b) => b.slotStart === null)
  }
}

export class WeeklyTrialsDropInsEntity {
  constructor(
    public readonly startDate: string,
    public readonly endDate: string,
    public readonly days: DayDataEntity[],
  ) {}

  static fromDto(
    daysDto: WeeklyTrialsDropInsBirthdaysDto,
    startDate: string,
    endDate: string,
  ): WeeklyTrialsDropInsEntity {
    // Create a map of existing days from API
    const dayMap = new Map(daysDto.map((d) => [d.dayDate, DayDataEntity.fromDto(d)]))

    // Generate all 7 days of the week, filling in missing days with empty data
    const allDays: DayDataEntity[] = []
    const start = dayjs(startDate)

    for (let i = 0; i < 7; i++) {
      const currentDate = start.add(i, "day").format("YYYY-MM-DD")
      const existingDay = dayMap.get(currentDate)

      if (existingDay) {
        allDays.push(existingDay)
      } else {
        // Create empty day for missing dates
        allDays.push(new DayDataEntity(currentDate, 0, 0, 0, [], [], []))
      }
    }

    return new WeeklyTrialsDropInsEntity(startDate, endDate, allDays)
  }

  getTotalTrials(): number {
    return this.days.reduce((sum, day) => sum + day.numberOfTrials, 0)
  }

  getTotalDropIns(): number {
    return this.days.reduce((sum, day) => sum + day.numberOfDropIns, 0)
  }

  getTotalBirthdays(): number {
    return this.days.reduce((sum, day) => sum + day.numberOfBirthdays, 0)
  }

  getWeekLabel(locale: Language): string {
    const start = dayjs(this.startDate)
    const end = dayjs(this.endDate)

    if (locale === Language.FRENCH) {
      return `${start.format("D MMM")} - ${end.format("D MMM")}`
    }
    return `${start.format("MMM D")} - ${end.format("MMM D")}`
  }

  isCurrentWeek(): boolean {
    const today = dayjs()
    const start = dayjs(this.startDate)
    const end = dayjs(this.endDate)
    return today.isSameOrAfter(start, "day") && today.isSameOrBefore(end, "day")
  }

  getSortedDays(locale: Language): DayDataEntity[] {
    if (locale === Language.ENGLISH_US) {
      const sunday = this.days.find((d) => dayjs(d.date).day() === 0)
      const restOfWeek = this.days.filter((d) => dayjs(d.date).day() !== 0)
      return sunday ? [sunday, ...restOfWeek] : this.days
    }
    return this.days
  }
}
